import React, { useEffect, useRef } from 'react'
import { ArrowRight, Briefcase, Mail, Linkedin, MapPin, Phone } from 'lucide-react'

const Hero = () => {
  const titleRef = useRef(null)
  
  useEffect(() => {
    const el = titleRef.current
    if (!el) return
    
    const chars = '!<>-_\\/[]{}—=+*^?#________'
    const originalText = 'Intelligent'
    let frame = 0
    
    const update = () => {
      let output = ''
      const progress = frame / 20
      
      for (let i = 0; i < originalText.length; i++) {
        if (i < Math.floor(progress * originalText.length)) {
          output += originalText[i]
        } else if (Math.random() < 0.28) {
          output += `<span class="text-primary">${chars[Math.floor(Math.random() * chars.length)]}</span>`
        } else {
          output += originalText[i]
        }
      }
      
      el.innerHTML = output
      frame++
      
      if (frame <= 20) {
        requestAnimationFrame(update)
      } else {
        el.textContent = originalText
      }
    }
    
    const timer = setTimeout(update, 1000)
    return () => clearTimeout(timer)
  }, [])
  
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-primary text-sm font-medium animate-pulse">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Available for Leadership Opportunities
            </div>
            
            <h1 className="text-5xl md:text-7xl font-display font-bold leading-tight text-white">
              Architecting <br />
              <span ref={titleRef} className="text-gradient">Intelligent</span> Futures
            </h1>
            
            <p className="text-xl text-gray-400 max-w-lg leading-relaxed">
              Senior Engineering Leader with <span className="text-white font-semibold">19+ years</span> architecting AI/ML solutions, cloud-native fintech platforms, and multi-agent autonomous systems that drive measurable business outcomes.
            </p>
            
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-3 py-1 rounded-full glass text-primary">AI/ML Architecture</span>
              <span className="px-3 py-1 rounded-full glass text-primary">Agentic Systems</span>
              <span className="px-3 py-1 rounded-full glass text-primary">Cloud-Native Fintech</span>
              <span className="px-3 py-1 rounded-full glass text-primary">MLOps</span>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="px-8 py-4 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-semibold hover:shadow-2xl hover:shadow-primary/25 transition-all transform hover:scale-105 flex items-center gap-2"
              >
                Connect With Me
                <ArrowRight size={20} />
              </a>
              <a
                href="#experience"
                className="px-8 py-4 rounded-full glass text-white font-semibold hover:bg-white/10 transition-all flex items-center gap-2"
              >
                View Experience
                <Briefcase size={20} />
              </a>
            </div>
            
            <div className="flex items-center gap-6 pt-4 text-gray-400 flex-wrap">
              <a href="mailto:jmaheshrao@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Mail size={18} />
                <span className="text-sm">jmaheshrao@gmail.com</span>
              </a>
              <a href="https://linkedin.com/in/jmaheshrao" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Linkedin size={18} />
                <span className="text-sm">LinkedIn</span>
              </a>
              <div className="flex items-center gap-2">
                <MapPin size={18} />
                <span className="text-sm">San Jose, CA</span>
              </div>
            </div>
          </div>
          
          <div className="hidden lg:flex justify-center">
            <div className="relative">
              <div className="w-96 h-96 rounded-full bg-gradient-to-br from-primary/30 to-secondary/30 blur-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              <div className="relative">
                {/* Profile Image */}
                <div className="relative mb-6">
                  <div className="w-64 h-64 mx-auto rounded-full bg-gradient-to-br from-primary to-secondary p-1">
                    <div className="w-full h-full rounded-full overflow-hidden bg-dark">
                      <img 
                        src="/images/profile.png" 
                        alt="Mahesh Jammalamadaka"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  {/* Decorative ring */}
                  <div className="absolute inset-0 w-64 h-64 mx-auto rounded-full border-2 border-primary/30 animate-pulse" />
                </div>
                
                {/* Stats Card */}
                <div className="glass rounded-2xl p-6 space-y-4">
                  <div className="text-center">
                    <h3 className="font-display font-bold text-xl text-white">Mahesh Jammalamadaka</h3>
                    <p className="text-primary">CTO & AI Engineering Leader</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gradient">19+</div>
                      <div className="text-xs text-gray-400">Years Experience</div>
                    </div>
                    <div className="glass rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gradient">1M+</div>
                      <div className="text-xs text-gray-400">Monthly Txns</div>
                    </div>
                    <div className="glass rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gradient">99.99%</div>
                      <div className="text-xs text-gray-400">Uptime</div>
                    </div>
                    <div className="glass rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gradient">30+</div>
                      <div className="text-xs text-gray-400">Engineers Led</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
